/**
 * Time utility functions consistent with practitioner calendar approach
 * These functions display times exactly as stored without timezone conversion
 */

/**
 * Format a Date as YYYY-MM-DD using its LOCAL calendar fields.
 *
 * Use this for the `date` query param of availability/booking endpoints.
 * `date.toISOString().split('T')[0]` is wrong: it converts to UTC first, so a
 * date picked as local midnight anywhere east of UTC (Asia/KL, and the UK during
 * BST) rolls back to the previous day — the customer picks the 20th and sees the
 * 19th's availability.
 */
export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  const parts = Object.fromEntries(
    dtf
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    parts.hour === '24' ? 0 : Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  )
  return asUTC - date.getTime()
}

/** UTC instant for a wall-clock date/time in the tenant timezone (mirrors backend helper). */
export function createBookingDateTimeInTimezone(
  dateStr: string,
  timeStr: string,
  timezone: string
): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  const timeParts = timeStr.split(':').map(Number)
  const hour = timeParts[0] || 0
  const minute = timeParts[1] || 0
  const second = timeParts[2] || 0
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  const offset1 = getTimeZoneOffsetMs(utcGuess, timezone)
  let result = new Date(utcGuess.getTime() - offset1)
  const offset2 = getTimeZoneOffsetMs(result, timezone)
  if (offset2 !== offset1) {
    result = new Date(utcGuess.getTime() - offset2)
  }
  return result
}

/** Format an ISO instant as `yyyy-MM-ddTHH:mm` for datetime-local in tenant timezone. */
export function isoToDateTimeLocalInTimezone(iso: string | Date, timezone: string): string {
  const date = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00'
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`
}

/** Parse datetime-local as tenant wall clock and return ISO UTC for the API. */
export function dateTimeLocalInTimezoneToIso(localValue: string, timezone: string): string {
  const [datePart, timePart] = localValue.split('T')
  if (!datePart || !timePart) return new Date(localValue).toISOString()
  return createBookingDateTimeInTimezone(datePart, timePart, timezone).toISOString()
}

/** Display label for maintenance timestamps in tenant timezone. */
export function formatDateTimeInTimezone(
  iso: string | Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }
): string {
  const date = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(date.getTime())) return String(iso)
  try {
    return date.toLocaleString(undefined, { ...options, timeZone: timezone })
  } catch {
    return date.toLocaleString(undefined, options)
  }
}

/**
 * Converts time to 12-hour format without timezone conversion
 * Handles Date objects, ISO datetime strings, and simple time strings
 *
 * @param time - Time as Date object, string in format "09:00" or "2025-11-11T09:00:00Z"
 * @returns Formatted time in "12:00 PM" format
 */
export const toTenantTimeString = (time: string | Date): string => {
  if (!time) return ''

  // Handle Date objects (from backend API)
  if (time instanceof Date) {
    const hours = time.getHours().toString().padStart(2, '0')
    const minutes = time.getMinutes().toString().padStart(2, '0')
    const hour = time.getHours()
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    return `${displayHour}:${minutes} ${period}`
  }

  // Handle string inputs
  let hours: string, minutes: string

  if (time.includes('T') && (time.includes('Z') || /\d{4}-\d{2}-\d{2}/.test(time))) {
    // Parse ISO datetime string like "2025-11-11T09:00:00Z" or "2025-11-11T09:00:00"
    // Extract time directly from the ISO string to avoid timezone conversion
    const timePart = time.split('T')[1].replace('Z', '').split('.')[0] // Remove milliseconds if present
    const [h, m] = timePart.split(':')
    hours = h
    minutes = m
  } else {
    // Parse simple time string like "09:00"
    const [h, m] = time.split(':')
    hours = h
    minutes = m
  }

  const hour = parseInt(hours, 10)
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
  return `${displayHour}:${minutes} ${period}`
}

/** Format an ISO instant as HH:mm in the tenant IANA timezone (matches availability API). */
export function formatWallClockInTimezone(iso: string | Date, timezone: string): string {
  const date = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/**
 * Formats a time range without timezone conversion
 *
 * @param startTime - Start time as Date object, string in format "09:00" or ISO datetime
 * @param endTime - End time as Date object, string in format "10:00" or ISO datetime
 * @returns Formatted time range like "9:00 AM - 10:00 AM"
 */
export const toTenantTimeRangeString = (startTime: string | Date, endTime: string | Date): string => {
  return `${toTenantTimeString(startTime)} - ${toTenantTimeString(endTime)}`
}

/**
 * Extracts just the time part from an ISO string without timezone conversion
 *
 * @param isoString - ISO datetime string
 * @returns Time string in "HH:mm" format
 */
export const extractTimeFromISO = (isoString: string): string => {
  if (!isoString) return ''
  const timePart = isoString.split('T')[1]?.replace('Z', '').split('.')[0]
  return timePart ? timePart.substring(0, 5) : '' // Return HH:mm
}

/** Extract YYYY-MM-DD from API booking timestamps without timezone shift. */
export const bookingDateFromApi = (value: string | Date | null | undefined): string => {
  if (!value) return ''
  if (value instanceof Date) return toLocalDateString(value)
  if (value.includes('T')) return value.split('T')[0]
  return value.slice(0, 10)
}

/** Extract HH:mm from API booking startTime without timezone shift. */
export const bookingTimeFromApi = (value: string | Date | null | undefined): string => {
  if (!value) return ''
  if (value instanceof Date) {
    const hours = String(value.getUTCHours()).padStart(2, '0')
    const minutes = String(value.getUTCMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }
  return extractTimeFromISO(value)
}

/**
 * Creates a date object with the time from an ISO string but without timezone conversion
 * This is useful when you need to work with Date objects but want to preserve the original time
 *
 * @param isoString - ISO datetime string
 * @param date - Base date to attach the time to (defaults to today)
 * @returns Date object with the original time preserved
 */
export const createDateWithTenantTime = (isoString: string, baseDate: Date = new Date()): Date => {
  if (!isoString) return baseDate

  const timePart = isoString.split('T')[1]?.replace('Z', '').split('.')[0]
  if (!timePart) return baseDate

  const [hours, minutes, seconds = '0'] = timePart.split(':')
  const result = new Date(baseDate)
  result.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10), 0)

  return result
}

/**
 * Compares two time values without timezone conversion
 *
 * @param time1 - First time as Date object or string
 * @param time2 - Second time as Date object or string
 * @returns -1 if time1 < time2, 0 if equal, 1 if time1 > time2
 */
export const compareTenantTimes = (time1: string | Date, time2: string | Date): number => {
  const minutes1 = timeToMinutes(time1)
  const minutes2 = timeToMinutes(time2)

  if (minutes1 < minutes2) return -1
  if (minutes1 > minutes2) return 1
  return 0
}

/**
 * Converts time to minutes since midnight for easy comparison
 *
 * @param time - Time as Date object, string in format "09:00" or ISO datetime
 * @returns Minutes since midnight
 */
export const timeToMinutes = (time: string | Date): number => {
  let hours: number, minutes: number

  // Handle Date objects (from backend API)
  if (time instanceof Date) {
    hours = time.getHours()
    minutes = time.getMinutes()
  } else {
    // Handle string inputs
    if (time.includes('T')) {
      const timePart = time.split('T')[1].replace('Z', '').split('.')[0]
      const [h, m] = timePart.split(':')
      hours = parseInt(h, 10)
      minutes = parseInt(m, 10)
    } else {
      const [h, m] = time.split(':')
      hours = parseInt(h, 10)
      minutes = parseInt(m, 10)
    }
  }

  return hours * 60 + minutes
}

/**
 * Calculates the extended session end time when treatment overlaps with session
 * Preserves total 120-minute session duration
 *
 * @param arrivalTime - Session start time in "HH:mm" format
 * @param treatmentStartTime - Treatment start time in "HH:mm" format
 * @param treatmentDurationMinutes - Duration of treatment in minutes
 * @returns Extended session end time in "HH:mm" format
 *
 * @example
 * calculateExtendedSessionEndTime("09:00", "09:30", 50)
 * // Returns "11:50" (session: 9:00-9:30, treatment: 9:30-10:20, session: 10:20-11:50)
 */
export const calculateExtendedSessionEndTime = (
  arrivalTime: string,
  treatmentStartTime: string,
  treatmentDurationMinutes: number
): string => {
  // Parse times to minutes since midnight
  const [arrivalHours, arrivalMins] = arrivalTime.split(':').map(Number)
  const [treatmentStartHours, treatmentStartMins] = treatmentStartTime.split(':').map(Number)

  const arrivalMinutes = arrivalHours * 60 + arrivalMins
  const treatmentStartMinutes = treatmentStartHours * 60 + treatmentStartMins

  // Calculate treatment end time
  const treatmentEndMinutes = treatmentStartMinutes + treatmentDurationMinutes

  // Session duration is 120 minutes (2 hours for "Thermae Welcome")
  const sessionDurationMinutes = 120

  // Calculate session part 1: Arrival to Treatment Start
  const sessionPart1Duration = treatmentStartMinutes - arrivalMinutes

  // Calculate remaining session time
  const remainingSessionTime = sessionDurationMinutes - sessionPart1Duration

  // Extended session end time = Treatment End + Remaining Session Time
  const extendedSessionEndMinutes = treatmentEndMinutes + remainingSessionTime

  // Convert back to HH:mm format
  const extendedEndHours = Math.floor(extendedSessionEndMinutes / 60)
  const extendedEndMins = extendedSessionEndMinutes % 60

  return `${extendedEndHours.toString().padStart(2, '0')}:${extendedEndMins.toString().padStart(2, '0')}`
}

/**
 * Calculates end time given start time and duration
 *
 * @param startTime - Start time in "HH:mm" format
 * @param durationMinutes - Duration in minutes
 * @returns End time in "HH:mm" format
 */
export const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [hours, mins] = startTime.split(':').map(Number)
  const totalMinutes = hours * 60 + mins + durationMinutes

  const endHours = Math.floor(totalMinutes / 60)
  const endMins = totalMinutes % 60

  return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`
}